/** Which breakdown tab a header belongs to — the same column key can mean subtly
 *  different things per tab (e.g. actual hours: on the activity's locations vs on
 *  the location itself), so the explanation resolves per scope first. */
export type ColumnInfoScope = 'location' | 'subcontractor' | 'activity' | 'workers';

/** Tab-specific explanations — checked before the shared ones. */
const SCOPED: Record<ColumnInfoScope, Record<string, string>> = {
  activity: {
    plannedWorkDays: 'plannedWorkDays',
    actualWorkDays: 'activityActualWorkDays',
  },
  location: {
    actualWorkDays: 'locationActualWorkDays',
  },
  workers: {
    // §8.7 estimates — the tab's note carries the full caveat.
    productivity: 'workerProductivity',
    actualWorkDays: 'workerActualWorkDays',
  },
  subcontractor: {
    // Work-days model: the contractor's figures are roll-ups of its activities.
    actualWorkDays: 'subcontractorActualWorkDays',
    activeWorkers: 'activeWorkers',
  },
};

/** Columns whose explanation is the same on every tab. */
const SHARED: Record<string, string> = {
  spi: 'spi',
  util: 'util',
  confidence: 'confidence',
};

/**
 * The localised one-line explanation for a column header (shown behind an ⓘ icon),
 * or `undefined` when the column has none. Copy lives in
 * `productivity.columnInfo.<key>` — meaning only, no formulas.
 */
export function columnInfo(
  scope: ColumnInfoScope,
  columnKey: string,
  t: (key: string) => string,
): string | undefined {
  const key = SCOPED[scope][columnKey] ?? SHARED[columnKey];
  return key ? t(`productivity.columnInfo.${key}`) : undefined;
}